import { useState } from "react";
import { adjustGoalWeight, UnauthorizedError } from "@/lib/clinician-api";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-863 (Ö-229) — the doctor corrects a value the PATIENT owns.
//
// Emelie 2026-09-23:
//   "Kirill ska kunna korrigera/sätta målvikt i admin."
//   "Ändringen kräver orsak och auditloggas med före/efter-värde."
//   "Målet uppdateras i appen och patienten får veta att läkaren har justerat det."
//
// Two things the form says out loud, because they are the whole reason this
// is not an ordinary field:
//
//   · the reason is compulsory — the server refuses without it, and a control
//     that let him press Save first would just turn that into a surprise;
//   · she will be told. He should know that before he saves, not after.
//
// Collapsed behind a link by default. The goal is hers, and a correction is
// the exception — an always-open editor next to her number would invite one.
function GoalWeightCorrectionForm({
  patientId,
  current,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  current: number | null;
  onSaved: () => void;
  onUnauthorized?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [kg, setKg] = useState(current == null ? "" : String(current));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An empty field means "remove the goal", which is a real outcome and not a
  // validation failure — `kg: null` is what the contract accepts for it.
  const parsedKg = kg.trim() === "" ? null : Number(kg);
  const kgValid =
    parsedKg === null ||
    (Number.isFinite(parsedKg) && parsedKg >= 20 && parsedKg <= 500);
  const unchanged = parsedKg === current;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await adjustGoalWeight(patientId, parsedKg, reason.trim());
      setOpen(false);
      setReason("");
      onSaved();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-textSecondary underline mt-xs"
      >
        Korrigera målvikt
      </button>
    );
  }

  return (
    <div className="mt-xs rounded-md border border-surfaceMuted p-sm">
      <p className="text-xs text-textSecondary mb-xs">
        Målvikten är patientens egen. Hon får veta att du har justerat den.
      </p>
      <label className="block text-xs text-textSecondary mb-xs">
        Ny målvikt (kg) — lämna tomt för att ta bort målet
        <input
          type="number"
          value={kg}
          onChange={(e) => setKg(e.target.value)}
          className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
        />
      </label>
      <label className="block text-xs text-textSecondary mb-sm">
        Orsak (obligatorisk, syns inte för patienten)
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
        />
      </label>
      <div className="flex gap-xs">
        <button
          type="button"
          disabled={busy || !reason.trim() || !kgValid || unchanged}
          onClick={() => void save()}
          className="px-sm py-xs rounded-md border border-surfaceMuted text-xs disabled:opacity-50"
        >
          {busy ? "Sparar…" : "Spara korrigering"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setOpen(false)}
          className="px-sm py-xs rounded-md text-xs text-textSecondary"
        >
          Avbryt
        </button>
      </div>
      {unchanged ? (
        <p className="text-xs text-textSecondary mt-xs">
          Samma värde som nu — ingenting att korrigera.
        </p>
      ) : null}
      {!kgValid ? (
        <p className="text-xs text-danger mt-xs">
          Ange en vikt mellan 20 och 500 kg.
        </p>
      ) : null}
      {error ? <p className="text-xs text-danger mt-xs">{error}</p> : null}
    </div>
  );
}

// TES-1040 — hidden in the admin's read-only support view: a correction of her goal weight is a write.
export const GoalWeightCorrection = hiddenWhenReadOnly(
  GoalWeightCorrectionForm,
);
